import React, { useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { useTheme, useMediaQuery, makeStyles, Typography, IconButton } from "@material-ui/core";
import { Grid, DialogActions, Dialog, DialogTitle, DialogContent } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import AdvancedEditProductParams from "./AdvancedEditProductParams";
import AdvancedEditProductScheduleCells from "./AdvancedEditProductScheduleCells";
import { emptyArray, emptyObject } from "constants/defaultValues";
import { get, set } from "@totalsoft/react-state-lens";
import ProductSchedule from "../ProductSchedule";
import productPageStyle from "features/products/styles";
import EditProductActions from "../EditProductActions";
import { Close } from "@material-ui/icons";
import EditInsuranceDetails from "../EditInsuranceDetails";

const useStyles = makeStyles(productPageStyle);

function AdvancedEditProduct(props) {
  const { t } = useTranslation();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const styleProps = useMemo(() => ({ fullScreen }), [fullScreen]);
  const classes = useStyles(styleProps);

  const { openEditProductLens, quote, productPeriodicities, productScheduleTypes, autoAddedInsuranceTypes } = props;
  const { productValidation, productDirtyInfo, onSave, onComputeSchedule, settingsLens } = props;

  const { productLens } = props;
  const product = productLens |> get || emptyObject;

  const handleClose = useCallback(() => {
    if (props.saving) return;
    (openEditProductLens |> set)();
  }, [openEditProductLens, props.saving]);

  const dialogPaperProps = useMemo(() => ({ style: { overflowY: "visible", maxHeight: "100vh" } }), []);

  const cellParamTemplates = useMemo(() => {
    const productScheduleType = productScheduleTypes.find((x) => x.id === product?.scheduleTypeId);
    return productScheduleType?.templates || emptyArray;
  }, [product?.scheduleTypeId, productScheduleTypes]);

  return (
    <>
      <Dialog maxWidth={false} fullScreen={fullScreen} open={true} onClose={handleClose} PaperProps={dialogPaperProps}>
        <DialogTitle>
          <Grid container justifyContent="space-between" alignItems="center">
            <Grid item>
              <Typography variant="h4">{t("Product.EditAdvancedProduct")}</Typography>
            </Grid>
            <Grid item>
              <IconButton onClick={handleClose}>
                <Close />
              </IconButton>
            </Grid>
          </Grid>
        </DialogTitle>
        <DialogContent dividers className={classes.advancedDialogContent}>
          <Grid container spacing={2} direction={fullScreen ? "column" : "row"}>
            <Grid item xs={fullScreen} className={classes.editProductParams}>
              <AdvancedEditProductParams
                quote={quote}
                productLens={productLens}
                productDirtyInfo={productDirtyInfo}
                productValidation={productValidation}
                productScheduleTypes={productScheduleTypes}
                productPeriodicities={productPeriodicities}
                settingsLens={settingsLens}
                autoAddedInsuranceTypes={autoAddedInsuranceTypes}
              />
            </Grid>
            <Grid item xs className={classes.advancedEditProductScheduleCells}>
              <AdvancedEditProductScheduleCells
                quote={quote}
                productLens={productLens}
                settingsLens={settingsLens}
                cellParamTemplates={cellParamTemplates}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <EditProductActions
            showAdvancedProductEditAction={false}
            openEditProductLens={openEditProductLens}
            quote={quote}
            productLens={productLens}
            productDirtyInfo={productDirtyInfo}
            settingsLens={settingsLens}
            onSave={onSave}
            onComputeSchedule={onComputeSchedule}
            loading={props.loading}
            saving={props.saving}
          />
        </DialogActions>
      </Dialog>
      {(settingsLens.showProductSchedule |> get) && (
        <ProductSchedule showLens={settingsLens.showProductSchedule} quoteProductId={product.quoteProductId} />
      )}
      {(settingsLens.showInsurancePage |> get) && (
        <EditInsuranceDetails
          showInsurancePage={settingsLens.showInsurancePage}
          addInsurance={productLens.addInsurance |> get}
          quoteId={quote.quoteId}
          quoteProductId={product.quoteProductId}
        />
      )}
    </>
  );
}

AdvancedEditProduct.propTypes = {
  openEditProductLens: PropTypes.object.isRequired,
  quote: PropTypes.object,
  productLens: PropTypes.object.isRequired,
  productDirtyInfo: PropTypes.object.isRequired,
  productValidation: PropTypes.object.isRequired,
  productScheduleTypes: PropTypes.array.isRequired,
  productPeriodicities: PropTypes.array.isRequired,
  onSave: PropTypes.func.isRequired,
  onComputeSchedule: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  saving: PropTypes.bool.isRequired,
  settingsLens: PropTypes.object.isRequired,
  autoAddedInsuranceTypes: PropTypes.bool.isRequired,
};

export default AdvancedEditProduct;
